import type { useI18n } from "../i18n/useI18n"
import type { ValidatorIssue } from "../schemas"

function getQuotedCardId(issue: ValidatorIssue): string | null {
  const quotedMatch = issue.message.match(/"([^"]+)"/u)
  if (quotedMatch) {
    return quotedMatch[1] ?? null
  }

  const pathMatch = issue.path?.match(/\[([^\]]+)\]/u)
  return pathMatch?.[1] ?? null
}

function getLastNumber(issue: ValidatorIssue): number | null {
  const matches = [...issue.message.matchAll(/(\d+)/gu)]
  const last = matches[matches.length - 1]?.[1]
  return last == null ? null : Number.parseInt(last, 10)
}

function getTwoNumbers(issue: ValidatorIssue): [number, number] | null {
  const matches = [...issue.message.matchAll(/(\d+)/gu)]
  if (matches.length < 2) return null
  const first = Number.parseInt(matches[0]?.[1] ?? "", 10)
  const second = Number.parseInt(matches[1]?.[1] ?? "", 10)
  return Number.isNaN(first) || Number.isNaN(second) ? null : [first, second]
}

export function getDeckValidationIssueLabel(
  t: ReturnType<typeof useI18n>["t"],
  issue: ValidatorIssue,
): string {
  switch (issue.code) {
    case "DECK_MISSING_HERO":
      return t("deckEditor.error.heroRequired")
    case "DECK_INVALID_MERC_COUNT": {
      const count = getLastNumber(issue)
      return count == null
        ? issue.message
        : t("deckEditor.error.exactCardCount", { count })
    }
    case "DECK_TOO_MANY_COPIES":
      return t("deckEditor.error.maxCopies", {
        cardId: getQuotedCardId(issue) ?? t("common.unknown"),
      })
    case "DECK_DUPLICATE_ENTRY":
      return t("deckEditor.validation.duplicateEntry", {
        cardId: getQuotedCardId(issue) ?? t("common.unknown"),
      })
    case "DECK_UNKNOWN_CARDS": {
      const count = getLastNumber(issue)
      return count == null
        ? issue.message
        : t("deckEditor.validation.unknownCards", { count })
    }
    case "DECK_TOO_FEW_LOW_COST": {
      const values = getTwoNumbers(issue)
      const actual = getLastNumber(issue)
      return values == null || actual == null
        ? issue.message
        : t("deckEditor.validation.tooFewLowCost", {
            min: values[0],
            threshold: values[1],
            count: actual,
          })
    }
    case "DECK_TOO_MANY_HIGH_COST": {
      const values = getTwoNumbers(issue)
      const actual = getLastNumber(issue)
      return values == null || actual == null
        ? issue.message
        : t("deckEditor.validation.tooManyHighCost", {
            max: values[0],
            threshold: values[1],
            count: actual,
          })
    }
    default:
      return issue.message
  }
}
